"use client";

import * as React from "react";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

import { cn } from "../../lib/utils";
import { getLocalStorageItem } from "../../helpers/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { DataTableViewOptions, columnLabel } from "./data-table-view-options";
import {
  DataTablePagination,
  PAGE_SIZE_OPTIONS,
} from "./data-table-pagination";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

// Columns declare a `meta.priority`: 1 is "identifying, always on screen" and
// higher numbers drop out first as the table narrows. Anything without a
// priority is treated as priority 1 so it is never collapsed by surprise.
export const DEFAULT_PRIORITY = 1;

// Widest priority still shown at a given container width. Ordered widest
// breakpoint first so the first match wins.
const PRIORITY_BREAKPOINTS = [
  { minWidth: 1024, maxPriority: Number.POSITIVE_INFINITY },
  { minWidth: 820, maxPriority: 3 },
  { minWidth: 640, maxPriority: 2 },
  { minWidth: 1, maxPriority: 1 },
];

export function maxVisiblePriority(width) {
  // Width 0/null means "not measured yet" (the very first paint, or jsdom),
  // where collapsing columns would only cause a flash of the wrong layout.
  if (!width) return Number.POSITIVE_INFINITY;
  const match = PRIORITY_BREAKPOINTS.find((bp) => width >= bp.minWidth);
  return match ? match.maxPriority : Number.POSITIVE_INFINITY;
}

// Mirrors how TanStack derives a column id from a column def, so we can work
// out responsive visibility before the table instance exists. A dotted
// accessor key ("report.total") becomes an id with underscores
// ("report_total"), so match that or a nested column would silently never
// collapse.
export function resolveColumnId(columnDef) {
  if (columnDef.id) return columnDef.id;
  if (typeof columnDef.accessorKey === "string") {
    return columnDef.accessorKey.replace(/\./g, "_");
  }
  return columnDef.accessorKey ?? null;
}

function useContainerWidth(ref) {
  const [width, setWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Clamped to the viewport on purpose. If an ancestor is sized by its
    // content rather than constraining it (a flex item left at the default
    // min-width:auto), this node reports far wider than the window and we
    // would conclude every column fits, silently disabling collapsing. The
    // layout should be fixed at the source, but believing an impossible
    // width is never right, so take the smaller of the two.
    const measure = () => {
      const measured = node.getBoundingClientRect().width;
      const viewport = window.innerWidth || measured;
      setWidth(Math.min(measured, viewport));
    };
    measure();

    // Both, not either. Because the width above depends on window.innerWidth,
    // a viewport change has to re-measure even when the node itself does not
    // resize -- and in the very layout the clamp guards against, the node's
    // width is driven by its content and stays put while the window shrinks,
    // so ResizeObserver alone would never fire and the clamp would go stale.
    window.addEventListener("resize", measure);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (observer) observer.observe(node);

    return () => {
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, [ref]);

  return width;
}

export const columnVisibilityStorageKey = (tableId) =>
  `moblyze:table-columns/v1/${tableId}`;

export const columnOrderStorageKey = (tableId) =>
  `moblyze:table-column-order/v1/${tableId}`;

const sameOrder = (a, b) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * Fold a saved column order onto the columns the table actually has today.
 *
 * A saved order outlives the column set it was written against: a release can
 * add a column, rename one or drop one, and the entry sitting in a recruiter's
 * localStorage still names the old set. So the saved order is treated as a
 * preference to apply where it still makes sense, never as the list of columns
 * to render.
 *
 *  - ids the table no longer has are dropped (and duplicates collapsed),
 *  - ids the saved order never knew about are put back where the column defs
 *    put them: straight after the nearest earlier column the saved order does
 *    know, or at the front when there is no such column.
 *
 * The result therefore always contains exactly the current columns, so a stale
 * or corrupt entry can reshuffle a table but can never blank one out.
 */
export function reconcileColumnOrder(savedOrder, naturalOrder) {
  if (!Array.isArray(savedOrder) || savedOrder.length === 0) return naturalOrder;

  const exists = new Set(naturalOrder);
  const placed = new Set();
  const order = [];

  savedOrder.forEach((id) => {
    if (typeof id !== "string" || !exists.has(id) || placed.has(id)) return;
    placed.add(id);
    order.push(id);
  });

  naturalOrder.forEach((id, index) => {
    if (placed.has(id)) return;
    let anchor = -1;
    for (let i = index - 1; i >= 0; i -= 1) {
      const at = order.indexOf(naturalOrder[i]);
      if (at !== -1) {
        anchor = at;
        break;
      }
    }
    order.splice(anchor + 1, 0, id);
    placed.add(id);
  });

  return order;
}

/**
 * Recruiter-facing data table built on the shadcn/ui data-table pattern.
 *
 * On top of the existing Chakra table it adds a "Columns" menu where each
 * column can be shown, hidden and moved left or right (both persisted per
 * table in localStorage) and a responsive mode where low-priority columns
 * collapse into an expandable row instead of squashing the layout, which is
 * what the Bullhorn iframe needs on a narrow screen.
 *
 * Pagination is opt-in and always server-side: pass `pagination`
 * ({ pageIndex, pageSize }), `onPaginationChange` and `rowCount` and the table
 * renders the pager and reports page changes upward so the caller can refetch.
 * With no `pagination` prop the table renders every row it is given, exactly
 * as it did before.
 *
 * A paginated caller can also pass `loading` while its fetch is in flight, and
 * the table shows that the page is on its way. It never blanks the rows to do
 * it: on a 346-page table, swapping the rows for a spinner would collapse the
 * page to nothing, throw the scroll position away and leave the reader with no
 * idea where they were. The rows they are looking at stay exactly where they
 * are, dimmed, under a progress strip.
 */
export default function DataTable({
  data,
  columns,
  searchInput,
  onClickRow,
  initialSort = [],
  tableId,
  pagination,
  onPaginationChange,
  rowCount = 0,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  loading = false,
}) {
  // Presence of a pagination state is what opts a table into the server-side
  // pager; the data prop is then just the current page.
  const isPaginated = Boolean(pagination);
  // Only the paginated path has a fetch to wait on. A jobs list renders its
  // rows straight from a prop it already holds, so it gets no indicator, no
  // live region and no dimming, whatever it happens to pass.
  const isLoading = isPaginated && Boolean(loading);
  const initialSortMemo = React.useMemo(() => initialSort, [initialSort]);

  const [sorting, setSorting] = React.useState(initialSortMemo);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [expandedRowIds, setExpandedRowIds] = React.useState({});

  const [columnVisibility, setColumnVisibility] = React.useState(() =>
    tableId ? getLocalStorageItem(columnVisibilityStorageKey(tableId), {}) : {}
  );

  // Several callers build their column defs inline, so `columns` is a brand new
  // array on every render. Memoizing on it would recompute every render anyway
  // while implying otherwise, so this is computed directly; the stability that
  // matters comes from `naturalOrder` below, keyed off this string (no column
  // id contains a comma). That is what keeps the reconciliation from re-running
  // for a column set that has not actually changed.
  const naturalOrderKey = columns
    .map(resolveColumnId)
    .filter(Boolean)
    .join(",");
  const naturalOrder = React.useMemo(
    () => (naturalOrderKey ? naturalOrderKey.split(",") : []),
    [naturalOrderKey]
  );

  const [columnOrder, setColumnOrder] = React.useState(() =>
    reconcileColumnOrder(
      tableId ? getLocalStorageItem(columnOrderStorageKey(tableId), null) : null,
      columns.map(resolveColumnId).filter(Boolean)
    )
  );

  // A saved order is only ever a preference over the current column set, so it
  // is folded back onto that set whenever the set changes.
  React.useEffect(() => {
    setColumnOrder((previous) => {
      const next = reconcileColumnOrder(previous, naturalOrder);
      return sameOrder(previous, next) ? previous : next;
    });
  }, [naturalOrder]);

  const containerRef = React.useRef(null);
  const containerWidth = useContainerWidth(containerRef);

  // if sorting is cleared, reset to initial sort. A paginated table is ordered
  // by the server, so it never restores a client-side sort.
  React.useEffect(() => {
    if (isPaginated) return;
    if (sorting.length === 0 && initialSortMemo.length > 0) {
      setSorting(initialSortMemo);
    }
  }, [isPaginated, sorting, initialSortMemo]);

  // if search input changes, update global filter
  React.useEffect(() => {
    setGlobalFilter(searchInput);
  }, [searchInput]);

  const handleColumnVisibilityChange = React.useCallback(
    (updater) => {
      setColumnVisibility((previous) => {
        const next =
          typeof updater === "function" ? updater(previous) : updater;
        if (tableId) {
          try {
            window.localStorage.setItem(
              columnVisibilityStorageKey(tableId),
              JSON.stringify(next)
            );
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[DataTable] Failed to persist column visibility", err);
          }
        }
        return next;
      });
    },
    [tableId]
  );

  const handleColumnOrderChange = React.useCallback(
    (updater) => {
      setColumnOrder((previous) => {
        const requested =
          typeof updater === "function" ? updater(previous) : updater;
        const next = reconcileColumnOrder(requested, naturalOrder);
        if (tableId) {
          try {
            window.localStorage.setItem(
              columnOrderStorageKey(tableId),
              JSON.stringify(next)
            );
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[DataTable] Failed to persist column order", err);
          }
        }
        return next;
      });
    },
    [tableId, naturalOrder]
  );

  // Columns the narrow layout has folded away. These stay in the table (so the
  // "Columns" menu keeps telling the truth about what the user chose) and are
  // skipped at render time instead.
  //
  // Note that this reads `meta.priority` off the column defs and never the
  // column order: where a column sits left to right and how early it folds
  // away are deliberately independent. Inferring priority from order would
  // mean that dragging a column to the front quietly kept a different one on
  // screen, so a recruiter reordering their table would see columns appear and
  // vanish for a reason nothing on screen explains. Priority governs
  // collapsing, `columnOrder` governs position, and neither moves the other.
  const collapsedColumnIds = React.useMemo(() => {
    const maxPriority = maxVisiblePriority(containerWidth);
    if (maxPriority === Number.POSITIVE_INFINITY) return [];

    return columns
      .filter(
        (columnDef) =>
          (columnDef.meta?.priority ?? DEFAULT_PRIORITY) > maxPriority
      )
      .map(resolveColumnId)
      .filter(Boolean);
  }, [columns, containerWidth]);

  const isCollapsed = React.useCallback(
    (columnId) => collapsedColumnIds.includes(columnId),
    [collapsedColumnIds]
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: handleColumnOrderChange,

    ...(isPaginated
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          manualPagination: true,
          onPaginationChange,
          rowCount,
          // The server decides the order, and sorting the rows of one page
          // would only reorder that page. So the sorted row model is left out
          // altogether rather than merely disabled: with no sorting state and
          // no sorted row model, a caller cannot reorder a page even by
          // passing `initialSort`.
          enableSorting: false,
        }
      : {
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
        }),

    state: {
      globalFilter,
      columnVisibility,
      columnOrder,
      ...(isPaginated ? { pagination } : { sorting }),
    },
    globalFilterFn: fuzzyFilter,
    onGlobalFilterChange: setGlobalFilter,
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  // Only worth an expander column when something is actually folded away.
  const hasCollapsedColumns =
    collapsedColumnIds.length > 0 &&
    table
      .getVisibleLeafColumns()
      .some((column) => isCollapsed(column.id));

  const visibleColumnCount =
    table.getVisibleLeafColumns().filter((column) => !isCollapsed(column.id))
      .length + (hasCollapsedColumns ? 1 : 0);

  // Only meaningful on the paginated path, and only read there.
  const totalPages = Math.max(table.getPageCount(), 1);
  // Clamped because pageIndex and rowCount arrive from the caller separately:
  // applying a filter while on a later page shrinks the count before the page
  // resets, and an unclamped value announces "Loading page 7 of 3".
  const currentPage = isPaginated
    ? Math.min(pagination.pageIndex + 1, totalPages)
    : 1;

  const toggleRow = (rowId) =>
    setExpandedRowIds((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }));

  return (
    <div ref={containerRef} className="w-full min-w-0">
      <div className="mb-2 flex justify-end">
        <DataTableViewOptions table={table} />
      </div>

      <div className="w-full overflow-hidden rounded-xl border border-moblyze-grey-border-dark bg-moblyze-grey-dark">
        <Table aria-busy={isPaginated ? isLoading : undefined}>
          <TableHeader>
            {headerGroups.map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {hasCollapsedColumns && (
                  <TableHead className="w-10 px-2">
                    <span className="sr-only">Show more</span>
                  </TableHead>
                )}
                {headerGroup.headers
                  .filter((header) => !isCollapsed(header.column.id))
                  .map((header) => {
                    const meta = header.column.columnDef.meta;
                    const sorted = header.column.getIsSorted();
                    return (
                      <TableHead
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className={cn(
                          "whitespace-nowrap",
                          meta?.isNumeric && "text-right",
                          // The column being sorted on is the one thing in the
                          // header that earns full contrast.
                          sorted && "text-white",
                          header.column.getCanSort() &&
                            "cursor-pointer hover:text-white"
                        )}
                      >
                        <span
                          className={cn(
                            "inline-flex items-center gap-1",
                            meta?.isNumeric && "justify-end"
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {sorted === "desc" && (
                            <ChevronDown
                              className="h-3.5 w-3.5 shrink-0"
                              aria-label="sorted descending"
                            />
                          )}
                          {sorted === "asc" && (
                            <ChevronUp
                              className="h-3.5 w-3.5 shrink-0"
                              aria-label="sorted ascending"
                            />
                          )}
                        </span>
                      </TableHead>
                    );
                  })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody
            className={cn(
              // The rows themselves are the only thing that changes when a
              // page lands, so they are the only thing dimmed: the header, the
              // pager and the card around them hold still. Rows stay mounted
              // and readable throughout, they just stop being the live thing
              // on screen, and they stop being clickable so a click aimed at
              // one candidate cannot land on the next page's occupant of that
              // row.
              isLoading && "pointer-events-none opacity-40",
              "transition-opacity duration-200"
            )}
          >
            {isPaginated && (
              // The rail the indeterminate strip runs along, pinned under the
              // header divider. It is a row of the table so it spans exactly
              // the columns on screen, and it stays mounted for the life of a
              // paginated table: it has no height of its own and the strip
              // inside it is taken out of flow, so a page starting or landing
              // moves nothing below it by even a pixel.
              <tr aria-hidden="true" className="h-0">
                <td
                  colSpan={visibleColumnCount || 1}
                  className="relative h-0 border-0 p-0 leading-none"
                >
                  {isLoading && (
                    <div
                      data-testid="table-loading-bar"
                      className="absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-moblyze-grey-border-dark/60"
                    >
                      <div className="h-full w-1/4 animate-indeterminate-bar bg-moblyze-green motion-reduce:w-full motion-reduce:animate-none" />
                    </div>
                  )}
                </td>
              </tr>
            )}
            {rows.map((row, index) => {
              const cells = row.getVisibleCells();
              const collapsedCells = cells.filter((cell) =>
                isCollapsed(cell.column.id)
              );
              const isExpanded = Boolean(expandedRowIds[row.id]);
              // Striped by position in the data, not in the DOM, so an open
              // detail row does not flip the banding of everything below it.
              const isStriped = index % 2 === 1;

              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      // Zebra striping gives every row a resting state, so a
                      // wide row stays readable even when the pointer is
                      // somewhere else.
                      isStriped && "bg-white/[0.03]",
                      // The expanded row below is part of the same block, so
                      // the pair shares a tint and drops the line between them.
                      isExpanded && "border-b-transparent bg-white/[0.06]",
                      // Hover and keyboard focus repaint the whole row, so a
                      // row under the pointer looks the same whichever band it
                      // happens to land on.
                      "hover:bg-white/[0.09] focus-within:bg-white/[0.09]",
                      onClickRow ? "cursor-pointer" : "cursor-default"
                    )}
                    onClick={
                      onClickRow
                        ? () => onClickRow(row?.original, searchInput)
                        : undefined
                    }
                  >
                    {hasCollapsedColumns && (
                      <TableCell className="w-10 px-2">
                        {collapsedCells.length > 0 && (
                          <button
                            type="button"
                            aria-label={
                              isExpanded ? "Hide details" : "Show details"
                            }
                            aria-expanded={isExpanded}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(row.id);
                            }}
                            className="flex h-6 w-6 items-center justify-center rounded text-moblyze-grey-light transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-moblyze-green focus-visible:ring-offset-0"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </TableCell>
                    )}
                    {cells
                      .filter((cell) => !isCollapsed(cell.column.id))
                      .map((cell) => {
                        const meta = cell.column.columnDef.meta;
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              "text-[14px] font-normal text-white",
                              meta?.isNumeric && "text-right"
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                  </TableRow>

                  {isExpanded && collapsedCells.length > 0 && (
                    // Same tint as the row it belongs to, and an accent down
                    // the left edge, so the details read as a continuation of
                    // that row rather than as a band of their own.
                    <TableRow className="bg-white/[0.06]">
                      <TableCell
                        colSpan={visibleColumnCount}
                        className="border-l-2 border-l-moblyze-green py-3 pl-4"
                      >
                        <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] items-baseline gap-x-4 gap-y-2 text-[14px] text-white">
                          {collapsedCells.map((cell) => (
                            <React.Fragment key={cell.id}>
                              <dt className="text-[11px] font-semibold uppercase tracking-wider text-moblyze-grey-light">
                                {columnLabel(cell.column) || cell.column.id}
                              </dt>
                              <dd>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}

            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount || 1}
                  className="py-6 text-center text-[14px] text-moblyze-grey-light"
                >
                  No records
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {isPaginated && table.getRowCount() > 0 && (
          // The pager lives inside the same bordered card as the table and on
          // the same surface as the header, so the two bookend the rows.
          <DataTablePagination
            table={table}
            pageSizeOptions={pageSizeOptions}
            loading={isLoading}
            className="border-t border-moblyze-grey-border-dark bg-moblyze-charcoal px-3 py-2.5"
          />
        )}
      </div>

      {isPaginated && (
        // One polite announcement per page change, and nothing else: the text
        // is built from the page number alone, so typing in a search box or
        // reordering a column never disturbs it.
        <div role="status" aria-live="polite" className="sr-only">
          {isLoading
            ? `Loading page ${currentPage} of ${totalPages}`
            : `Page ${currentPage} of ${totalPages}`}
        </div>
      )}
    </div>
  );
}
